import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import {
	type ConnectorProps,
	ConnectorSources,
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--rovo-expansion/search-page-results-inline';

import { SearchPageInline3PBanner } from '../../../components/SearchPageInline3PBanner';

import { type MessageTemplateContext } from '../../../index';

import { messages } from '../../../messages';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type MessageFrontendProps = MessageTemplateContext & PlacementRenderProps;

/** Feature gate for testing */
const SHORT_SNOOZE_TEST_GATE = 'PO_enable_rovo-search-inline-3p-short-snooze-test';

/**
 * Snooze durations in days for each dismissal in the cycle.
 * 1st dismiss → 2 weeks, 2nd → 1 month, 3rd → 1 month, 4th → 3 months and reset.
 */
const SNOOZE_DAYS = [14, 30, 30, 90] as const;

/**
 * Short snooze durations (in minutes) when SHORT_SNOOZE_TEST_GATE is on - for testing only.
 */
const SHORT_SNOOZE_MINUTES = [3, 3, 3, 5] as const;

/** Fixed snooze duration for "Connect later" - 2 weeks (or 3 mins when short-snooze gate is on) */
const CONNECT_LATER_SNOOZE_DAYS = 14;

const SHORT_CONNECT_LATER_SNOOZE = 3;

/**
 * Returns the connector after the last dismissed one, or the first connector if none dismissed.
 */
const selectConnectorForBanner = (
	availableConnectors: MessageFrontendProps['availableConnectors'],
	lastDismissed3P: string,
): ConnectorProps | null => {
	const lastIndex = availableConnectors.findIndex((c) => c.connectorId === lastDismissed3P);
	// Start searching from the connector after the last dismissed one
	const candidates = availableConnectors.slice(lastIndex + 1);
	return candidates.find((c) => c.userNeedsOAuth) ?? null;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageFrontendProps>(),
		Component: ({
			dismiss,
			availableConnectors,
			useOAuthHandlers,
			onMessageViewed,
			productSource,
			lastDismissed3P,
			dismissalCount,
			fullConnectorVariant,
			smartLinkVariant,
			...rest
		}: MessageFrontendProps) => {
			const { messageInstanceViewed } = useMessageInstanceEvent();
			const { onSnoozed } = usePostOfficeUserInteractions();
			const { formatMessage } = useIntl();
			const connector = selectConnectorForBanner(availableConnectors, lastDismissed3P);

			if (!connector) {
				return null;
			}

			const { connectorId, connectorMode } = connector;
			const variant =
				connectorMode === ConnectorSources.Smartlinks ? smartLinkVariant : fullConnectorVariant;

			// Ref keeps the callback reference stable so the effect only re-runs when the
			// actual data values change, not when onMessageViewed gets a new reference.
			// The effect calls onMessageViewed with a trackImpression function, letting the
			// caller decide if/when to trigger messageInstanceViewed.
			const onMessageViewedRef = useRef(onMessageViewed);
			onMessageViewedRef.current = onMessageViewed;
			useEffect(() => {
				if (!onMessageViewedRef.current || !messageInstanceViewed) {
					return;
				}
				onMessageViewedRef.current(() => {
					void messageInstanceViewed({
						productSource,
						connectorSource: connectorMode,
						connectorId,
						variant,
					});
				});
			}, [messageInstanceViewed, connectorId, connectorMode, productSource, variant]);

			const handleDismiss = useCallback(() => {
				if (!connector) {
					return;
				}

				const useShortSnooze = FeatureGates.checkGate(SHORT_SNOOZE_TEST_GATE);
				const snoozeDuration = useShortSnooze
					? (SHORT_SNOOZE_MINUTES[dismissalCount] ?? SHORT_SNOOZE_MINUTES[0])
					: (SNOOZE_DAYS[dismissalCount] ?? SNOOZE_DAYS[0]);

				void onSnoozed({
					interactionIdentifier: 'dismiss-3p-banner',
					durationDays: snoozeDuration,
					additionalMetadata: {
						actionReason: 'dismiss',
						dismissedConnectorId: connector.connectorId,
						dismissedVariant: variant,
						variant,
						productSource,
						connectorSource: connector.connectorMode,
						...(useShortSnooze && { snoozeUnit: 'minutes' as const }),
					},
				});

				dismiss();
			}, [onSnoozed, dismiss, dismissalCount, connector, productSource, variant]);

			const handleConnectLater = useCallback(() => {
				if (!connector) {
					return;
				}

				const useShortSnooze = FeatureGates.checkGate(SHORT_SNOOZE_TEST_GATE);
				const snoozeDuration = useShortSnooze
					? SHORT_CONNECT_LATER_SNOOZE
					: CONNECT_LATER_SNOOZE_DAYS;

				void onSnoozed({
					interactionIdentifier: 'connect-later-3p-banner',
					durationDays: snoozeDuration,
					additionalMetadata: {
						actionReason: 'connect-later',
						connectorId: connector.connectorId,
						variant,
						productSource,
						connectorSource: connector.connectorMode,
						...(useShortSnooze && { snoozeUnit: 'minutes' as const }),
					},
				});

				dismiss();
			}, [onSnoozed, dismiss, connector, productSource, variant]);

			const { onAuthorize } = useOAuthHandlers({
				product: connector.connectorProductKey,
				connectorSource: connector.connectorMode,
				successFlagTitle: formatMessage(messages.authorizationSuccess, {
					product: connector.connectorName,
				}),
				failFlagTitle: formatMessage(messages.authorizationFail, {
					product: connector.connectorName,
				}),
				outboundAuthUrl: connector.oAuthUrl,
				extraAttributes: {
					connectorId: connector.connectorId,
					variant,
				},
			});

			return (
				<ManualMessageViewedEvent>
					<SearchPageInline3PBanner
						context={{
							productSource,
							onAuthorize,
							variant,
							connector,
							teamUsagePercentage: rest.teamUsagePercentage,
						}}
						onDismiss={handleDismiss}
						onConnectLater={handleConnectLater}
					/>
				</ManualMessageViewedEvent>
			);
		},
	},
});
